import { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Clock, MapPin, Car, Baby, Accessibility, Coffee,
  CheckCircle, Calendar, ArrowRight, Users, Music, Heart, Sparkles, PartyPopper, Phone, Gift
} from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useSite } from '../../context/SiteContext'
import { useVisitForm } from '../../hooks/useForm'
import { visitSchema } from '../../utils/validators'
import { Input, Select, Textarea } from '../../components/common'

const whatToExpect = [
  {
    icon: Clock,
    title: 'Service Length',
    description: 'Our services typically last about 2 hours, including worship and the message.',
    gradient: 'from-primary-500 to-blue-500',
  },
  {
    icon: Music,
    title: 'Worship Style',
    description: 'Contemporary and traditional blend with passionate, Spirit-led worship.',
    gradient: 'from-secondary-500 to-purple-500',
  },
  {
    icon: Users,
    title: 'Friendly People',
    description: "You'll be warmly greeted but not put on the spot. Come as you are!",
    gradient: 'from-accent-500 to-orange-500',
  },
  {
    icon: Coffee,
    title: 'Refreshments',
    description: 'Enjoy complimentary coffee and snacks in our lobby before and after service.',
    gradient: 'from-amber-500 to-yellow-500',
  },
  {
    icon: Baby,
    title: "Children's Ministry",
    description: 'Safe, fun programs for kids from nursery through elementary school.',
    gradient: 'from-rose-500 to-pink-500',
  },
  {
    icon: Accessibility,
    title: 'Accessibility',
    description: 'Wheelchair accessible with reserved seating and assistive listening devices.',
    gradient: 'from-emerald-500 to-teal-500',
  },
]

const perks = [
  { title: 'Reserved Guest Parking', description: 'Park close to the entrance', icon: Car },
  { title: 'Personal Greeter', description: 'Someone to show you around', icon: Users },
  { title: 'Welcome Gift', description: 'A special gift just for you', icon: Gift },
]

const VisitForm = () => {
  const { submit, submitting, submitted, error, reset } = useVisitForm()
  const { settings } = useSite()

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset: resetForm,
  } = useForm({
    resolver: zodResolver(visitSchema),
  })

  const onSubmit = async (data) => {
    const result = await submit(data)
    if (result.success) {
      resetForm()
    }
  }

  if (submitted) {
    return (
      <motion.div
        className="text-center py-12"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <motion.div
          className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", bounce: 0.5 }}
        >
          <PartyPopper className="w-10 h-10 text-white" />
        </motion.div>
        <h3 className="text-2xl font-heading font-bold text-dark-100 mb-2">
          We Can't Wait to Meet You!
        </h3>
        <p className="text-dark-300 mb-6 max-w-md mx-auto">
          You're all set! Look for a confirmation email with details about your visit.
          We'll have a special welcome ready for you!
        </p>
        <motion.button
          onClick={reset}
          className="px-6 py-3 rounded-xl border-2 border-primary-500 text-primary-600 font-semibold hover:bg-primary-50 transition-colors"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Register Another Family
        </motion.button>
      </motion.div>
    )
  }

  const serviceOptions = settings.serviceTimes?.map(s => ({
    value: `${s.day} ${s.time}`,
    label: `${s.name} - ${s.day} ${s.time}`,
  })) || []

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="grid md:grid-cols-2 gap-6">
        <Input
          label="Your Name"
          required
          error={errors.name?.message}
          {...register('name')}
        />
        <Input
          label="Email Address"
          type="email"
          required
          error={errors.email?.message}
          {...register('email')}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Input
          label="Phone Number"
          type="tel"
          required
          error={errors.phone?.message}
          {...register('phone')}
        />
        <Input
          label="Planned Visit Date"
          type="date"
          required
          error={errors.visitDate?.message}
          {...register('visitDate')}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Select
          label="Which Service?"
          required
          error={errors.serviceTime?.message}
          options={serviceOptions}
          {...register('serviceTime')}
        />
        <Select
          label="How Many in Your Party?"
          required
          error={errors.familySize?.message}
          options={[
            { value: '1', label: 'Just me' },
            { value: '2', label: '2 people' },
            { value: '3-4', label: '3-4 people' },
            { value: '5+', label: '5 or more' },
          ]}
          {...register('familySize')}
        />
      </div>

      <Input
        label="Ages of Children (if any)"
        placeholder="e.g., 3, 7, 12"
        {...register('childrenAges')}
      />

      <Select
        label="How Did You Hear About Us?"
        required
        error={errors.howHeard?.message}
        options={[
          { value: 'friend', label: 'Friend or Family' },
          { value: 'google', label: 'Google Search' },
          { value: 'social', label: 'Social Media' },
          { value: 'drove_by', label: 'Drove By' },
          { value: 'event', label: 'Community Event' },
          { value: 'other', label: 'Other' },
        ]}
        {...register('howHeard')}
      />

      <Textarea
        label="Any Questions or Special Needs?"
        rows={3}
        {...register('questions')}
        placeholder="Let us know if you have any questions or need accommodations..."
      />

      {error && (
        <motion.p
          className="text-red-500 text-sm bg-red-50 px-4 py-2 rounded-xl"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {error}
        </motion.p>
      )}

      <motion.button
        type="submit"
        disabled={submitting}
        className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-accent-500 to-accent-600 text-white font-bold shadow-lg shadow-accent-500/25 disabled:opacity-50"
        whileHover={{ scale: 1.02, y: -2 }}
        whileTap={{ scale: 0.98 }}
      >
        {submitting ? (
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : (
          <Calendar className="w-5 h-5" />
        )}
        Pre-Register My Visit
      </motion.button>
    </form>
  )
}

const PlanVisit = () => {
  const { settings, getNextService, getGoogleMapsUrl, getFullAddress } = useSite()
  const nextService = getNextService()

  return (
    <>
      {/* Hero Section */}
      <section className="relative py-32 md:py-40 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-dark-100 to-dark" />
          <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-accent-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-primary-500/20 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <motion.span
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm font-semibold mb-6 border border-white/20"
              whileHover={{ scale: 1.05 }}
            >
              <Sparkles className="w-4 h-4 text-accent-400" />
              We're Expecting You
            </motion.span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-heading font-bold text-white mb-6">
              Plan Your <span className="bg-gradient-to-r from-accent-400 to-orange-400 bg-clip-text text-transparent">Visit</span>
            </h1>
            <p className="text-xl text-white/70">
              We want your first visit to be a great experience. Here's everything you need to know!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Quick Info Cards */}
      <section className="py-8 -mt-20 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-4">
            {/* Next Service */}
            <motion.div
              className="bg-white rounded-2xl p-6 shadow-soft hover:shadow-soft-lg transition-all"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ y: -5 }}
            >
              <div className="flex items-center gap-4 mb-4">
                <motion.div
                  className="w-14 h-14 bg-gradient-to-br from-accent-500 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                >
                  <Clock className="w-7 h-7 text-white" />
                </motion.div>
                <div>
                  <h3 className="font-heading font-bold text-dark-100">Next Service</h3>
                  <p className="text-sm text-dark-300">{nextService?.name}</p>
                </div>
              </div>
              <p className="text-2xl font-heading font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                {nextService?.day} at {nextService?.time}
              </p>
            </motion.div>

            {/* Location */}
            <motion.div
              className="bg-white rounded-2xl p-6 shadow-soft hover:shadow-soft-lg transition-all"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              whileHover={{ y: -5 }}
            >
              <div className="flex items-center gap-4 mb-4">
                <motion.div
                  className="w-14 h-14 bg-gradient-to-br from-primary-500 to-blue-500 rounded-2xl flex items-center justify-center shadow-lg"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                >
                  <MapPin className="w-7 h-7 text-white" />
                </motion.div>
                <div>
                  <h3 className="font-heading font-bold text-dark-100">Location</h3>
                  <p className="text-sm text-dark-300">Easy to find</p>
                </div>
              </div>
              <a
                href={getGoogleMapsUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-semibold group"
              >
                Get Directions
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </motion.div>

            {/* Parking */}
            <motion.div
              className="bg-white rounded-2xl p-6 shadow-soft hover:shadow-soft-lg transition-all"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              whileHover={{ y: -5 }}
            >
              <div className="flex items-center gap-4 mb-4">
                <motion.div
                  className="w-14 h-14 bg-gradient-to-br from-secondary-500 to-purple-500 rounded-2xl flex items-center justify-center shadow-lg"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                >
                  <Car className="w-7 h-7 text-white" />
                </motion.div>
                <div>
                  <h3 className="font-heading font-bold text-dark-100">Parking</h3>
                  <p className="text-sm text-dark-300">Free & convenient</p>
                </div>
              </div>
              <p className="text-dark-300 text-sm">
                First-time guest parking near the main entrance!
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-24 bg-gradient-to-b from-light-100 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <motion.span
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-100 text-primary-700 text-sm font-semibold mb-4"
              whileHover={{ scale: 1.05 }}
            >
              <Heart className="w-4 h-4" fill="currentColor" />
              What to Expect
            </motion.span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-dark-100 mb-4">
              Your First <span className="gradient-text">Visit</span>
            </h2>
            <p className="text-lg text-dark-300 max-w-2xl mx-auto">
              Here's a preview of what you can expect when you walk through our doors.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whatToExpect.map((item, index) => (
              <motion.div
                key={item.title}
                className="bg-white rounded-2xl p-6 shadow-soft hover:shadow-soft-lg transition-all group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <motion.div
                  className={`w-14 h-14 bg-gradient-to-br ${item.gradient} rounded-2xl flex items-center justify-center mb-4 shadow-lg`}
                  whileHover={{ scale: 1.1, rotate: 5 }}
                >
                  <item.icon className="w-7 h-7 text-white" />
                </motion.div>
                <h3 className="font-heading font-bold text-lg text-dark-100 mb-2 group-hover:text-primary-600 transition-colors">
                  {item.title}
                </h3>
                <p className="text-dark-300">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pre-Registration Form */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Info Side */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <motion.span
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary-100 text-secondary-700 text-sm font-semibold mb-4"
                whileHover={{ scale: 1.05 }}
              >
                <Calendar className="w-4 h-4" />
                Pre-Register
              </motion.span>
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-dark-100 mb-6">
                Let Us Know <span className="gradient-text">You're Coming!</span>
              </h2>
              <p className="text-lg text-dark-300 mb-10">
                Pre-registration helps us prepare a warm welcome for you. We'll have your name
                tag ready and a special gift waiting!
              </p>

              <div className="space-y-5 mb-10">
                {perks.map((perk, index) => (
                  <motion.div
                    key={perk.title}
                    className="flex items-start gap-4 bg-gradient-to-r from-emerald-50 to-teal-50 p-4 rounded-xl border border-emerald-100"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                      <perk.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-heading font-bold text-dark-100">{perk.title}</h4>
                      <p className="text-dark-300 text-sm">{perk.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Map Preview */}
              <div className="rounded-2xl overflow-hidden shadow-soft h-52 border-4 border-white">
                <iframe
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(getFullAddress())}&output=embed`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  title="Church Location"
                />
              </div>
            </motion.div>

            {/* Form Side */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-white rounded-3xl shadow-soft p-8 border border-gray-100 sticky top-24">
                <VisitForm />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-secondary-500 via-primary-500 to-accent-500" />
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6">
              Questions?
            </h2>
            <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
              We're here to help make your visit as comfortable as possible.
              Don't hesitate to reach out!
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <motion.button
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-white text-primary-600 font-bold shadow-xl"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Contact Us
                  <ArrowRight className="w-5 h-5" />
                </motion.button>
              </Link>
              <a href={`tel:${settings.phone}`}>
                <motion.button
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl border-2 border-white text-white font-bold hover:bg-white/10 transition-colors"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Phone className="w-5 h-5" />
                  Call {settings.phone}
                </motion.button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  )
}

export default PlanVisit
